import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import minorHomeBg from "assets/images/others/minor-side-images/minorBg.png";
import useStore from "hooks/useStore";
import { EventDTO } from "utils/helpers/models/shared/event.dto";
import useMinor from "../useHooks";
import EventCard from "components/cards/event-card";
import { siteRoutes } from "utils/helpers/enums/routes";
import { useSelector } from "react-redux";
import CustomMessageDisplay from "components/data-not-found/CustomMessageDisplay";
import eventNotFoundGif from "assets/images/gifs/event-not-found.gif";

const MinorHome: React.FC = () => {
  const navigate = useNavigate();
  const [upcomingEvents, setUpcomingEvents] = useState<EventDTO[]>([]);
  const [recommendedEvents, setRecommendedEvents] = useState<EventDTO[]>([]);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const { getMinorHome } = useMinor();
  const {  getUserFullName } = useStore();
  const { isLoading } = useSelector((state: any) => state.sharedReducer);
  const userFullName = getUserFullName();

  useEffect(() => {
    getMinorHome(setRecommendedEvents, setUpcomingEvents);
  }, []);

  // Mark initial load as complete after first data load
  useEffect(() => {
    if (isInitialLoad && !isLoading) {
      setIsInitialLoad(false);
    }
  }, [isLoading, upcomingEvents, recommendedEvents]);

  // Sort events by start date (nearest first)
  const sortEventsByDate = (events: EventDTO[]) => {
    return [...events].sort((a, b) => {
      const getEarliestDate = (event: EventDTO) => {
        if (event.workshopSessions && event.workshopSessions.length > 0) {
          // Get the earliest workshop session date
          const dates = event.workshopSessions
            .map((session: any) => {
              if (session.startDateTime) {
                const date = new Date(session.startDateTime);
                return isNaN(date.getTime()) ? null : date;
              }
              return null;
            })
            .filter((date) => date !== null);

          if (dates.length > 0) {
            return Math.min(...dates.map((date) => date!.getTime()));
          }
        }
        // Fallback to event creation date or current date
        return new Date().getTime();
      };

      const aDate = getEarliestDate(a);
      const bDate = getEarliestDate(b);

      return aDate - bDate;
    });
  };

  // Get sorted events
  const sortedUpcomingEvents = sortEventsByDate(upcomingEvents);
  const sortedRecommendedEvents = sortEventsByDate(recommendedEvents);

  return (
    <div>
      <div
        style={{ backgroundImage: `url(${minorHomeBg})` }}
        className="bg-cover bg-center bg-no-repeat h-full md:h-[300px] w-full"
      >
        <div className="minor-bg-gradient h-full ">
          <div className="container">
            <div className="flex justify-between items-center flex-wrap md:flex-nowrap mt-8">
              <div className="flex flex-col gap-2">
                <p className="text-2xl text-primaryBlueText">Welcome</p>
                <h2 className="text-4xl text-primaryBlueText capitalize">
                  {userFullName}!
                </h2>
                <p className="text-[21px] text-primaryBlueText font-medium mt-3">
                  Build skills and confidence in a fun, hands-on way.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container min-h-[70vh]">
        <div className="">
          {/* Show Recommended Events */}
          {sortedRecommendedEvents?.length > 0 && (
            <div className="mt-5 md:mt-10">
              <h2 className="text-2xl font-bold mb-4">Recommended Events</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
                {sortedRecommendedEvents.map((event, index) => (
                  <EventCard event={event} key={index} />
                ))}
              </div>

              {sortedRecommendedEvents?.length >= 12 && (
                <div className="text-center mt-10">
                  <button
                    onClick={() => navigate(siteRoutes.minorExperiences)}
                    className="px-6 py-2 bg-fade text-primaryBlueText rounded-full"
                  >
                    See More
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="">
          {/* Show Upcoming Events */}
          {sortedUpcomingEvents?.length > 0 && (
            <div
              className={
                sortedRecommendedEvents?.length > 0 ? "my-5 md:my-10" : ""
              }
            >
              <h2 className="text-2xl font-bold mb-4">Upcoming Events</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
                {sortedUpcomingEvents.map((event, index) => (
                  <EventCard event={event} key={index} showBookmark={false} />
                ))}
              </div>

              {sortedUpcomingEvents?.length >= 12 && (
                <div className="text-center mt-10">
                  <button
                    onClick={() => navigate(siteRoutes.minorExperiences)}
                    className="px-6 py-2 bg-fade text-primaryBlueText rounded-full"
                  >
                    See More
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-center h-full min-h-[50vh]">
          <CustomMessageDisplay
            show={
              !isLoading &&
              !isInitialLoad &&
              !sortedUpcomingEvents.length &&
              !sortedRecommendedEvents.length
            }
            title="No Experiences Available"
            slogan="No experiences available at this moment."
            image={eventNotFoundGif}
            imageAlt="No events found"
            className="py-16"
            imageClassName="w-[150px] h-[120px]"
            titleClassName="text-2xl font-bold text-blackie"
            sloganClassName="text-gray-500 text-base"
          />
        </div>
      </div>
    </div>
  );
};

export default MinorHome;
